﻿import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AdminCard, AdminErrorBanner, AdminLoadingState, AdminPageLayout } from "@/components/admin";
import AlertBanner from "./components/AlertBanner";
import DashboardSummaryCards from "./components/DashboardSummaryCards";
import RiskUsersSection from "./components/RiskUsersSection";
import EventStream from "./components/EventStream";
import DashboardQuickStatus from "./components/DashboardQuickStatus";
import { ApiError, fetchDashboardRecentDeviceIssues, fetchDashboardRecentEmergencyEvents, fetchDashboardSummary } from "../../../lib/api";
import { formatDateTime } from "@/lib/format";
import type { DashboardDeviceIssue, DashboardSummary, EmergencyEventItem } from "@/types.ts";
const AdminDashboard = () => {
    const navigate = useNavigate();
    const [summary, setSummary] = useState<DashboardSummary | null>(null);
    const [events, setEvents] = useState<EmergencyEventItem[]>([]);
    const [deviceIssues, setDeviceIssues] = useState<DashboardDeviceIssue[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    useEffect(() => {
        let ignore = false;
        const loadDashboard = async () => {
            setIsLoading(true);
            setErrorMessage("");
            try {
                const [summaryData, eventData, deviceIssueData] = await Promise.all([
                    fetchDashboardSummary(),
                    fetchDashboardRecentEmergencyEvents(8),
                    fetchDashboardRecentDeviceIssues(5),
                ]);
                if (ignore)
                    return;
                setSummary(summaryData);
                setEvents(eventData);
                setDeviceIssues(deviceIssueData);
            }
            catch (error) {
                if (ignore)
                    return;
                if (error instanceof ApiError && error.status === 401) {
                    navigate("/admin/login", { replace: true });
                    return;
                }
                setErrorMessage(error instanceof Error ? error.message : "대시보드 데이터를 불러오지 못했습니다.");
            }
            finally {
                if (!ignore) {
                    setIsLoading(false);
                }
            }
        };
        void loadDashboard();
        return () => {
            ignore = true;
        };
    }, [navigate]);
    const activeEventCount = (summary?.openEmergencyEventCount ?? 0) +
        (summary?.acknowledgedEmergencyEventCount ?? 0) +
        (summary?.inProgressEmergencyEventCount ?? 0);
    const processingCount = (summary?.acknowledgedEmergencyEventCount ?? 0) + (summary?.inProgressEmergencyEventCount ?? 0);
    const totalEvents = Math.max(summary?.todayEmergencyEventCount ?? 0, activeEventCount);
    const handledCount = Math.max(totalEvents - activeEventCount, 0);
    const handlingRate = totalEvents > 0 ? Math.round((handledCount / totalEvents) * 100) : 100;
    const shouldShowAlert = activeEventCount > 0 || (summary?.todayNoResponseCount ?? 0) > 0;
    const workboxItems = [
        {
            label: "긴급 관제",
            description: "미처리·처리 중 긴급 이벤트",
            count: activeEventCount,
            to: "/admin/emergency",
            icon: "ri-alarm-warning-line",
            tone: "bg-red-50 text-red-700 border-red-100",
        },
        {
            label: "생활신호 확인",
            description: "무응답·주의 필요 케어 흐름",
            count: summary?.todayNoResponseCount ?? 0,
            to: "/admin/life-signal",
            icon: "ri-heart-pulse-line",
            tone: "bg-emerald-50 text-emerald-700 border-emerald-100",
        },
        {
            label: "과거 소리 v2 위험",
            description: "오늘 위험 소리 후보 이벤트",
            count: summary?.todayRiskSoundCount ?? 0,
            to: "/admin/sound-analysis",
            icon: "ri-sound-module-line",
            tone: "bg-violet-50 text-violet-700 border-violet-100",
        },
        {
            label: "오프라인 기기",
            description: "최근 heartbeat 미수신 기기",
            count: summary?.offlineDeviceCount ?? 0,
            to: "/admin/devices",
            icon: "ri-router-line",
            tone: "bg-slate-50 text-slate-700 border-slate-200",
        },
    ];
    const progressItems = useMemo(() => [
        { label: "처리 완료", value: `${handledCount}건`, color: "text-emerald-600", dot: "bg-emerald-500" },
        { label: "확인 중", value: `${processingCount}건`, color: "text-orange-600", dot: "bg-orange-500" },
        { label: "미처리", value: `${summary?.openEmergencyEventCount ?? 0}건`, color: "text-red-600", dot: "bg-red-500" },
        { label: "전체 이벤트", value: `${totalEvents}건`, color: "text-slate-700", dot: "bg-slate-600" },
    ], [handledCount, processingCount, summary?.openEmergencyEventCount, totalEvents]);
    return (<AdminPageLayout
          title="대시보드"
          hasIssue={shouldShowAlert || (summary?.offlineDeviceCount ?? 0) > 0}
          issueLabel="확인 필요"
        >
          <div className="mt-4">
            {isLoading ? (<AdminLoadingState message="대시보드 데이터를 불러오는 중입니다."/>) : errorMessage ? (<AdminErrorBanner message={errorMessage}/>) : (<>
                {shouldShowAlert && (<AlertBanner urgentCount={activeEventCount} warningCount={summary?.todayNoResponseCount ?? 0}/>)}

                <section className="mb-4">
                  <DashboardSummaryCards summary={summary}/>
                </section>

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                  <div className="xl:col-span-2 flex flex-col gap-4">
                    <AdminCard className="p-4">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <i className="ri-inbox-2-line text-blue-600 text-sm"/>
                            <h3 className="text-sm font-semibold text-slate-800">통합 업무함</h3>
                          </div>
                          <p className="mt-1 text-xs text-slate-500">운영자가 지금 먼저 처리해야 할 항목을 최종 메뉴 기준으로 묶었습니다.</p>
                        </div>
                        <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-700">
                          {workboxItems.reduce((sum, item) => sum + item.count, 0)}건
                        </span>
                      </div>
                      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                        {workboxItems.map((item) => (<Link key={item.label} to={item.to} className={`flex items-center justify-between rounded-2xl border px-4 py-3 transition-opacity hover:opacity-80 ${item.tone}`}>
                            <div className="flex min-w-0 items-center gap-3">
                              <i className={`${item.icon} text-lg`}/>
                              <div className="min-w-0">
                                <p className="truncate text-sm font-bold">{item.label}</p>
                                <p className="truncate text-xs opacity-80">{item.description}</p>
                              </div>
                            </div>
                            <span className="text-lg font-black">{item.count}</span>
                          </Link>))}
                      </div>
                    </AdminCard>
                    <RiskUsersSection events={events}/>
                    <EventStream events={events}/>
                  </div>

                  <div className="xl:col-span-1 flex flex-col gap-4">
                    <DashboardQuickStatus summary={summary}/>

                    <AdminCard className="p-4">
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <i className="ri-router-line text-slate-500 text-sm"/>
                          <h3 className="text-sm font-semibold text-slate-800">최근 기기 이상</h3>
                        </div>
                        <Link className="text-xs font-semibold text-blue-600 hover:text-blue-700" to="/admin/devices">전체 보기</Link>
                      </div>
                      <div className="space-y-2">
                        {deviceIssues.map((issue) => (
                          <Link key={issue.deviceId} to={`/admin/devices?seniorUserAccountId=${issue.seniorUserAccountId}&deviceId=${issue.deviceId}`} className="block rounded-xl border border-slate-100 bg-slate-50 p-3 transition-colors hover:border-blue-200 hover:bg-blue-50/40">
                            <div className="flex items-center justify-between gap-2">
                              <p className="truncate text-xs font-bold text-slate-700">{issue.elderlyName} · {issue.deviceName ?? issue.deviceUuid}</p>
                              <span className="shrink-0 text-xs font-semibold text-red-600">{issue.deviceStatus}</span>
                            </div>
                            <p className="mt-1 text-xs text-slate-400">최근 연결 {formatDateTime(issue.lastOnlineAt)}</p>
                          </Link>
                        ))}
                        {deviceIssues.length === 0 && <p className="text-xs text-slate-500">최근 기기 이상이 없습니다.</p>}
                      </div>
                    </AdminCard>

                    <AdminCard className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <i className="ri-pie-chart-line text-slate-500 text-sm"/>
                        <h3 className="text-sm font-semibold text-slate-800">이벤트 처리 현황</h3>
                      </div>

                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs text-slate-500">처리율</span>
                          <span className="text-xs font-bold text-slate-800">{handlingRate}%</span>
                        </div>
                        <div className="w-full h-2 bg-slate-100 rounded overflow-hidden">
                          <div className="h-full rounded bg-emerald-500" style={{ width: `${handlingRate}%` }}/>
                        </div>
                      </div>

                      <div className="space-y-2">
                        {progressItems.map((item) => (<div key={item.label} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${item.dot}`}/>
                              <span className="text-xs text-slate-600">{item.label}</span>
                            </div>
                            <span className={`text-xs font-semibold ${item.color}`}>{item.value}</span>
                          </div>))}
                      </div>
                    </AdminCard>

                    <AdminCard className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <i className="ri-flashlight-line text-slate-500 text-sm"/>
                        <h3 className="text-sm font-semibold text-slate-800">빠른 이동</h3>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                { to: "/admin/emergency", icon: "ri-alarm-warning-line", label: "긴급 이벤트", color: "bg-red-50 text-red-600 border-red-100" },
                { to: "/admin/life-signal", icon: "ri-heart-pulse-line", label: "생활 신호", color: "bg-emerald-50 text-emerald-600 border-emerald-100" },
                { to: "/admin/users", icon: "ri-user-heart-line", label: "Senior 상세", color: "bg-blue-50 text-blue-600 border-blue-100" },
                { to: "/admin/devices", icon: "ri-router-line", label: "기기 관리", color: "bg-slate-50 text-slate-600 border-slate-200" },
            ].map((item) => (<Link key={item.to} to={item.to} className={`flex items-center gap-2 px-3 py-2.5 rounded border text-xs font-medium transition-opacity hover:opacity-80 ${item.color}`}>
                            <i className={`${item.icon} text-sm`}/>
                            {item.label}
                          </Link>))}
                      </div>
                    </AdminCard>
                  </div>
                </div>
              </>)}
          </div>
    </AdminPageLayout>);
};
export default AdminDashboard;
